/**
 * /dashboard/people — read-only company directory (Phase 3c —
 * 2026-06-01).
 *
 * Was the People page in the Next.js app. The directory itself is over
 * GET /api/v1/directory (auth-only for ANY tenant user — a line manager
 * or plain employee, not just HR; NOT feature-gated; non-PII roster
 * fields only). The "Add employee" / "Edit" affordances are role-gated
 * (company_admin / hr_manager) — the backend remains the boundary and
 * 403s a non-HR caller hitting POST/PATCH /employees regardless.
 *
 * Search filters by name/designation/department; a department <Select>
 * scopes the list. Each row links the person's manager to that
 * manager's row (in-page anchor) so the reporting line is navigable.
 *
 * Pagination: ?page=N (default 1) with a fixed per-page of 10. The
 * backend GET /api/v1/directory now accepts ?page=N&per_page=M and
 * returns total so the UI can show Prev/Next controls and a
 * "Showing X–Y of Z" count. Search/dept filters are client-side on the
 * current page's results (a natural UX tradeoff for small-roster SMBs).
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/people/page.tsx. Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link (typed). The Edit link
 *     uses params: { id } against the typed route
 *     /dashboard/people/$id/edit (registered in the same batch).
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { z } from "zod";
import { ChevronLeft, ChevronRight, Edit3, Search, UserPlus, Users } from "lucide-react";
import { useDirectory } from "@/hooks/use-directory";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import type { Role } from "@/types/auth";
import { toApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/page-header";
import { ModuleNav, type ModuleNavItem } from "@/components/shared/module-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ALL_DEPTS = "__all__";
const DEFAULT_PAGE_SIZE = 10;

// Mirrors the backend RBAC on POST /api/v1/employees (company_admin,
// hr_manager). Gates only the "Add employee" affordance — the directory
// itself is visible to every tenant user via GET /api/v1/directory.
const CAN_ADD_PEOPLE = new Set<Role>(["company_admin", "hr_manager"]);

const peopleSearchSchema = z.object({
  page: z.number().int().min(1).optional(),
});

export const Route = createFileRoute("/_authgated/app/$tenantSlug/people/")({
  validateSearch: peopleSearchSchema,
  component: PeopleRoute,
});

function PeopleRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const navigate = Route.useNavigate();
  const { page: urlPage } = Route.useSearch();
  const page = urlPage ?? 1;

  const canAdd = !!user?.role && CAN_ADD_PEOPLE.has(user.role);
  const hrAdmin = isHRAdmin(user?.role);

  const navItems = useMemo<ModuleNavItem[]>(() => {
    const base: ModuleNavItem[] = [
      { label: "Directory", href: `/app/${tenantSlug}/people` },
    ];
    if (hrAdmin) {
      base.push({ label: "Org chart", href: `/app/${tenantSlug}/people/org-chart` });
    }
    return base;
  }, [tenantSlug, hrAdmin]);
  // include_inactive so a deactivated colleague still resolves (e.g. as
  // someone's recorded manager); we visually flag them as Inactive.
  const dir = useDirectory({
    includeInactive: true,
    page,
    perPage: DEFAULT_PAGE_SIZE,
  });
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState<string>(ALL_DEPTS);

  const departments = useMemo(() => {
    const s = new Set<string>();
    for (const e of dir.entries) {
      if (e.department) s.add(e.department);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [dir.entries]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dir.entries
      .filter((e) => {
        if (dept !== ALL_DEPTS && (e.department ?? "") !== dept) {
          return false;
        }
        if (!q) return true;
        const hay = [
          e.display_name,
          e.designation ?? "",
          e.department ?? "",
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        // Active first, then alphabetical by display name.
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return a.display_name.localeCompare(b.display_name);
      });
  }, [dir.entries, search, dept]);

  const totalPages = Math.max(1, Math.ceil(dir.total / DEFAULT_PAGE_SIZE));
  // Showing range based on full total, adjusted for current page.
  const rangeStart = dir.total === 0 ? 0 : (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * DEFAULT_PAGE_SIZE, dir.total);
  const employeePlural = dir.total === 1 ? "employee" : "employees";

  function goToPage(p: number) {
    navigate({
      // Reducer form (the router types `search` as a ParamsReducerFn here).
      // page:undefined drops it from the URL (back to the default page 1),
      // matching the optional `page?: number` search schema.
      search: (prev) => ({ ...prev, page: p === 1 ? undefined : p }),
      replace: false,
    });
    // Reset client-side filters when paging so user isn't confused.
    setSearch("");
    setDept(ALL_DEPTS);
  }

  return (
    <>
      <PageHeader
        title="People"
        description="Your company directory — names, roles and reporting lines."
        actions={
          canAdd ? (
            <Button asChild className="gap-1.5">
              <Link to="/app/$tenantSlug/people/new" params={{ tenantSlug }}>
                <UserPlus className="h-4 w-4" />
                Add employee
              </Link>
            </Button>
          ) : undefined
        }
      />

      <ModuleNav items={navItems} />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, role or team"
            className="pl-8"
            aria-label="Search the directory"
          />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DEPTS}>All departments</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {dir.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }, (_, i) => `skel-${i}`).map((k) => (
            <Card key={k}>
              <CardHeader>
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="mt-2 h-4 w-1/2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {dir.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the directory</AlertTitle>
          <AlertDescription>
            {toApiError(dir.error).message}
          </AlertDescription>
        </Alert>
      )}

      {!dir.isLoading && !dir.isError && dir.total === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Users className="h-6 w-6" />
            </span>
            <p className="max-w-sm text-sm text-muted-foreground">
              {canAdd
                ? "It's just you so far. Add your team so they can use leave, expenses, POSH and more."
                : "No colleagues yet. As your HR team adds employees they will appear here."}
            </p>
            {canAdd && (
              <Button asChild className="gap-1.5">
                <Link to="/app/$tenantSlug/people/new" params={{ tenantSlug }}>
                  <UserPlus className="h-4 w-4" />
                  Add your first employee
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!dir.isLoading && !dir.isError && dir.total > 0 && (
        <>
          {/* Total count / range indicator */}
          <p
            className="mb-3 text-sm text-muted-foreground"
            aria-live="polite"
            data-testid="people-count"
          >
            {search || dept !== ALL_DEPTS
              ? `${rows.length} of ${dir.total} employees match`
              : `Showing ${rangeStart}–${rangeEnd} of ${dir.total} ${employeePlural}`}
          </p>

          {rows.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No people match these filters.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {rows.map((e) => (
                <Card key={e.id} id={`person-${e.id}`}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">
                          {e.display_name}
                          {e.id === dir.myId && (
                            <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-secondary-foreground">
                              You
                            </span>
                          )}
                        </CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {e.designation || "—"}
                          {e.department ? ` · ${e.department}` : ""}
                        </p>
                        {e.manager_id != null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Reports to{" "}
                            <a
                              href={`#person-${e.manager_id}`}
                              className="font-medium text-primary underline-offset-4 hover:underline"
                            >
                              {dir.label(e.manager_id, null, false)}
                            </a>
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {!e.is_active && (
                          <StatusBadge tone="neutral" label="Inactive" />
                        )}
                        {canAdd && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                          >
                            <Link
                              to="/app/$tenantSlug/people/$id/edit"
                              params={{ tenantSlug, id: String(e.id) }}
                            >
                              <Edit3 aria-hidden className="h-3.5 w-3.5" />
                              Edit
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination controls — only shown when more than one page */}
          {totalPages > 1 && (
            <div
              className="mt-6 flex items-center justify-between gap-4"
              aria-label="Pagination"
            >
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>

              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
                aria-label="Next page"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
